import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { TmAccount, TmCreditCard } from '@/tscapi/model/account';
import http from '@/tscapi/service/httpservice';
import secureHttpClient, { ensureSecureSession } from '@/tscapi/service/secure-httpservice';
import { Cookie } from '@/tscapi/utils/cookie';
import { bubbleUnexpectedErrorsOnly, SessionExpiredError } from '@/tscapi/utils/session';
import { getApiBaseUrl, getCommerceApiBaseUrlFull } from '@/tscapi/utils/utils';

interface PayloadType {
    creditcard: any;
    Id?: number;
}
interface HostedSessionPayloadType {
    HostedSessionId: string;
    ClientIp?: string;
    ReCaptchaToken: string;
    IsDefault?: boolean;
    BillingAddress?: {
        CardHolderName?: string;
        Street?: string;
        City?: string;
        Province?: string;
        Country?: string;
        PostalCode?: string;
    };
}

// used to create/add a TSC credit card using secure commerce api
export const useAddCreditCard2 = () => {
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    const queryClient = useQueryClient();
    return {
        ...useMutation({
            mutationFn: async (payload: PayloadType) => {
                const customerEdp = cookie.CustomerEdp;
                if (!(await ensureSecureSession()) || !customerEdp || customerEdp <= 0) {
                    throw new SessionExpiredError();
                }
                const base = getCommerceApiBaseUrlFull();
                return secureHttpClient.post(`${base}/${lang}/accounts/${customerEdp}/creditcards`, payload.creditcard);
            },
            onSuccess: async (response, variables, context) => {
                await queryClient.invalidateQueries({
                    queryKey: ['noPersist_creditcards', cookie.CustomerEdp, cookie.Language],
                });
            },
        }),
    };
};

// used to create/add a TSC credit card using secure commerce api & hostedSessionId
export const useAddCreditCard3 = () => {
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    const queryClient = useQueryClient();
    return {
        ...useMutation({
            mutationFn: async (payload: HostedSessionPayloadType) => {
                const customerEdp = cookie.CustomerEdp;
                if (!(await ensureSecureSession()) || !customerEdp || customerEdp <= 0) {
                    throw new SessionExpiredError();
                }
                const base = getCommerceApiBaseUrlFull();
                return secureHttpClient.post(`${base}/${lang}/accounts/${customerEdp}/creditcards`, payload);
            },
            onSuccess: async (response, variables, context) => {
                await queryClient.invalidateQueries({
                    queryKey: ['noPersist_creditcards', cookie.CustomerEdp, cookie.Language],
                });
            },
        }),
    };
};

// used to remove credit card
//
//
export const useRemoveCreditCard = () => {
    const queryClient = useQueryClient();
    const base = '/api/v2/';
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    return {
        ...useMutation({
            mutationFn: (Id: number) => {
                const userAccessTokenEdp = cookie.UserAccessToken?.Edp;
                if (
                    !(
                        cookie.UserAccessToken != undefined &&
                        cookie.UserAccessToken.Token.length > 0 &&
                        cookie.UserAccessToken.Edp > 0
                    )
                ) {
                    throw new Error('UserToken expired');
                }
                return http.delete(`${base}${lang}/accounts/${userAccessTokenEdp}/creditcards/${Id}`);
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: ['noPersist_creditcards', cookie.UserAccessToken?.Edp, cookie.Language],
                });
            },
            onError: (error, variables, context) => {
                console.log(`${context} ${error}`);
            },
        }),
    };
};

// used to remove credit card
//
//
export const useRemoveCreditCard2 = () => {
    const queryClient = useQueryClient();
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    return {
        ...useMutation({
            mutationFn: async (Id: number) => {
                const customerEdp = cookie.CustomerEdp;
                if (!(await ensureSecureSession()) || !customerEdp || customerEdp <= 0) {
                    throw new SessionExpiredError();
                }
                const base = getCommerceApiBaseUrlFull();
                return secureHttpClient.delete(`${base}/${lang}/accounts/${customerEdp}/creditcards/${Id}`);
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: ['noPersist_creditcards', cookie.CustomerEdp, cookie.Language],
                });
            },
            onError: (error, variables, context) => {
                console.log(`${context} ${error}`);
            },
        }),
    };
};

// used to update credit card
//
//
export const useUpdateCreditCard = () => {
    const queryClient = useQueryClient();
    const base = '/api/v2/';
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    return {
        ...useMutation({
            mutationFn: (payload: PayloadType) => {
                const userAccessTokenEdp = cookie.UserAccessToken?.Edp;
                if (
                    !(
                        cookie.UserAccessToken != undefined &&
                        cookie.UserAccessToken.Token.length > 0 &&
                        cookie.UserAccessToken.Edp > 0
                    )
                ) {
                    throw new Error('UserToken expired');
                }
                return http.put(
                    `${base}${lang}/accounts/${userAccessTokenEdp}/creditcards/${payload.Id}`,
                    payload.creditcard,
                );
            },
            onSuccess: async (response, variables, context) => {
                await queryClient.invalidateQueries({
                    queryKey: ['noPersist_creditcards', cookie.UserAccessToken?.Edp, cookie.Language],
                });
            },
            onError: (error, variables, context) => {
                console.log(`${context} ${error}`);
            },
        }),
    };
};
// used to update credit card
//
//
export const useUpdateCreditCard2 = () => {
    const queryClient = useQueryClient();
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    return {
        ...useMutation({
            mutationFn: async (payload: PayloadType) => {
                const customerEdp = cookie.CustomerEdp;
                if (!(await ensureSecureSession()) || !customerEdp || customerEdp <= 0) {
                    throw new SessionExpiredError();
                }
                const base = getCommerceApiBaseUrlFull();
                return secureHttpClient.put(
                    `${base}/${lang}/accounts/${customerEdp}/creditcards/${payload.Id}`,
                    payload.creditcard,
                );
            },
            onSuccess: async (response, variables, context) => {
                await queryClient.invalidateQueries({
                    queryKey: ['noPersist_creditcards', cookie.CustomerEdp, cookie.Language],
                });
            },
            onError: (error, variables, context) => {
                console.log(`${context} ${error}`);
            },
        }),
    };
};

// used to get all credit card
//
//
export const useCreditCards = (enabled = true) => {
    const cookie: Cookie = new Cookie();
    const userAccessToken = cookie.UserAccessToken;
    const isAuthenticated = Boolean(userAccessToken?.Token && userAccessToken.Edp > 0);
    return useQuery({
        queryKey: ['noPersist_creditcards', cookie.UserAccessToken?.Edp, cookie.Language],
        queryFn: fetchCreditCards,
        useErrorBoundary: true,
        enabled: isAuthenticated && enabled,
        retry: false,
        refetchOnWindowFocus: false,
    });
    async function fetchCreditCards(): Promise<TmCreditCard[]> {
        const cookie: Cookie = new Cookie();
        const base = '/api/v2/';
        const lang = cookie.Language.substring(0, 2) || 'en';
        const userAccessTokenEdp = cookie.UserAccessToken?.Edp;
        try {
            const response = await http.get<TmCreditCard[]>(
                `${base}${lang}/accounts/${userAccessTokenEdp}/creditcards`,
            );
            return response.data;
        } catch (error) {
            //if this state happens, user will be re-directed to signin page anyway
            console.log('Error getting creditCards');
            throw new Error('Error getting creditCards');
        }
    }
};

export const useCreditCards2 = (enabled = true) => {
    const cookie: Cookie = new Cookie();
    const customerEdp = cookie.CustomerEdp ?? cookie.UserAccessToken?.Edp;
    return useQuery({
        queryKey: ['noPersist_creditcards', cookie.CustomerEdp, cookie.Language],
        queryFn: fetchCreditCards,
        useErrorBoundary: bubbleUnexpectedErrorsOnly,
        enabled,
        retry: false,
        refetchOnWindowFocus: false,
    });
    async function fetchCreditCards(): Promise<TmCreditCard[]> {
        const cookie: Cookie = new Cookie();
        const base = getCommerceApiBaseUrlFull();
        const lang = cookie.Language.substring(0, 2) || 'en';
        // Checked outside the try/catch below so SessionExpiredError propagates as-is
        // instead of being swallowed and re-wrapped into a generic Error.
        if (!(await ensureSecureSession())) {
            throw new SessionExpiredError();
        }
        const currentCustomerEdp = cookie.CustomerEdp ?? customerEdp;
        if (!currentCustomerEdp || currentCustomerEdp <= 0) {
            throw new SessionExpiredError('Secure session account unavailable');
        }
        try {
            const endpoint = `${base}/${lang}/accounts/${currentCustomerEdp}/creditcards`;
            const response = await secureHttpClient.get<TmCreditCard[]>(endpoint);
            return response.data;
        } catch (error) {
            // Already handled upstream (redirect/webview hand-off) - don't re-wrap into a generic error.
            if (error instanceof SessionExpiredError) {
                throw error;
            }
            //if this state happens, user will be re-directed to signin page anyway
            console.log('Error getting creditCards');
            throw new Error('Error getting creditCards');
        }
    }
};
